import { Button, Table, Tooltip } from 'antd'
import {
  ExclamationCircleOutlined,
  FileSearchOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  EditOutlined,
  AlertOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useCallback, useMemo } from 'react'
import { useTheme } from 'react-jss'
import { Link } from 'react-router-dom'
import { useAsync } from '../../hooks'
import useQueryParams from '../../hooks/useQueryParams'
import useLocalStorage from '../../hooks/useLocalStorage'
import {
  useAlerts,
  useAlertsPagination,
  useFetchAlerts,
} from '../../redux/slices/alerts'
import { toPath } from '../../routes'
import { ListError, PageHeader } from '../Common'
import AddNewItemButton from '../Common/AddNewItemButton'
import DiseasesTags from '../Common/DiseasesTags'
import Filters, { FIELDS_OPTIONS } from '../Forms/Filters'
import { useIsAdmin, useIsPractitioner } from '../../redux/slices/session'
import { formatDOB } from '../../lib/datetime'
import { PAGE_SIZE, PAGE_SIZE_OPTIONS } from '../../lib/constants'

const DEFAULT_FILTERS = {
  acknowledged: FIELDS_OPTIONS.acknowledged[0].id,
  query: '',
}
const QUERY_PARAMS_SCHEMA = { page: Number, perPage: Number }

export default function AlertsList() {
  const { brandDanger, brandSuccess } = useTheme()
  const isAdmin = useIsAdmin()
  const isPractitioner = useIsPractitioner()
  const [conditions, setConditions] = useQueryParams({
    initialValues: DEFAULT_FILTERS,
    schema: QUERY_PARAMS_SCHEMA,
  })
  // eslint-disable-next-line no-unused-vars
  const [localStoragePageSize, setPageSize] = useLocalStorage(
    'pageSize',
    PAGE_SIZE,
  )
  const pageSize = conditions.perPage || localStoragePageSize
  const fromPracticeParam = useMemo(
    () => (conditions.practiceId ? '?fromPractice=true' : ''),
    [conditions],
  )

  const columns = useMemo(
    () => [
      {
        title: <ExclamationCircleOutlined style={{ color: brandDanger }} />,
        dataIndex: 'critical',
        key: 'critical',
        align: 'center',
        render(_text, { critical }) {
          return (
            critical && (
              <Tooltip title="Requires Attention">
                <ExclamationCircleOutlined style={{ color: brandDanger }} />
              </Tooltip>
            )
          )
        },
      },
      {
        title: 'Full Name',
        key: 'fullName',
        render(_text, alert) {
          return (
            <Link to={toPath.patients.patient({ id: alert.patientSummary.id })}>
              {alert.patientSummary.lastName}, {alert.patientSummary.firstName}
            </Link>
          )
        },
      },
      {
        title: 'MRN',
        dataIndex: 'patientSummary.practiceMrn',
        key: 'MRN',
      },
      {
        title: 'DOB',
        dataIndex: 'patientSummary.birthDate',
        key: 'dob',
        render: formatDOB,
      },
      {
        title: 'Stroke Prevention',
        key: 'diseases',
        render(text, alert) {
          const { patientSummary } = alert
          return <DiseasesTags patientSummary={patientSummary} />
        },
      },
      {
        title: 'Device Type',
        dataIndex: ['device', 'deviceModel', 'deviceType', 'name'],
        key: 'deviceType',
      },
      {
        title: 'Created At',
        key: 'createdAt',
        dataIndex: 'createdAt',
        render(text) {
          return dayjs(text).tz().calendar(null, {
            sameElse: 'MMM D, YYYY h:mm A',
          })
        },
      },
      {
        align: 'center',
        title: 'Acknowledged',
        key: 'acknowledged',
        dataIndex: 'acknowledgedAt',

        render: acknowledgedAtText =>
          acknowledgedAtText ? (
            <CheckCircleFilled style={{ color: brandSuccess }} />
          ) : (
            <CloseCircleFilled style={{ color: brandDanger }} />
          ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render(text, alert) {
          return (
            <div style={{ display: 'flex' }}>
              <Link
                to={`${toPath.alerts.alert({
                  id: alert.id,
                })}${fromPracticeParam}`}
              >
                <Button type="link" icon={<FileSearchOutlined />} />
              </Link>
              <Link
                to={toPath.alerts.alert.edit({ id: alert.id })}
                style={{ display: 'block' }}
              >
                <Button type="link" icon={<EditOutlined />} />
              </Link>
            </div>
          )
        },
      },
    ],
    [brandDanger, brandSuccess, fromPracticeParam],
  )
  const { pending, error, clearError } = useAsync(
    useFetchAlerts({ ...conditions, perPage: pageSize }),
  )

  const handlePageChange = useCallback(
    page => setConditions({ ...conditions, page }),
    [conditions, setConditions],
  )

  const onShowSizeChange = useCallback(
    (current, size) => {
      setPageSize(size)
      setConditions({ ...conditions, page: current, perPage: size })
    },
    [conditions, setConditions, setPageSize],
  )

  const handleSubmit = useCallback(
    ({
      practiceId,
      acknowledged,
      medicalProviderId,
      deviceTypeId,
      officeLocationId,
      query,
    }) => {
      const payload = {
        page: 1,
        practiceId,
        acknowledged,
        medicalProviderId,
        deviceTypeId,
        query,
      }
      if (practiceId === conditions.practiceId)
        payload.officeLocationId = officeLocationId
      setConditions(payload)
    },
    [conditions.practiceId, setConditions],
  )

  const { total } = useAlertsPagination()
  const alerts = useAlerts()
  return (
    <div>
      {error && <ListError description={error.message} onClose={clearError} />}
      <PageHeader
        title="Alerts"
        extra={
          isAdmin && (
            <AddNewItemButton
              itemName="alert"
              path={toPath.alerts.new()}
              icon={<AlertOutlined />}
            />
          )
        }
      />
      {(isAdmin || isPractitioner) && (
        <Filters
          currentValues={conditions}
          onSubmit={handleSubmit}
          showPractice={isAdmin}
          showAcknowledged
          showDeviceType
          showMedicalProvider
          showOfficeLocation
          showSearch
          placeholders={{
            query: 'Search by name, serial number or practice MRN',
          }}
        />
      )}
      <Table
        columns={columns}
        dataSource={alerts}
        loading={pending}
        pagination={{
          current: conditions.page || 1,
          pageSize: Number(pageSize),
          onChange: handlePageChange,
          showSizeChanger: true,
          onShowSizeChange,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          total,
        }}
        rowKey="id"
      />
    </div>
  )
}
